import { ecartJours, formaterJour, formaterMontant, jourAParis } from '@admin-pmvb/core'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { type FormEvent, useState } from 'react'
import { LogoConnu } from '../composants/Logo'
import { api, ErreurApi, lire, requetes } from '../api'
import { formaterInstant } from '../composants/Historique'
import { Icone } from '../composants/Icone'
import { Vide } from '../composants/Section'
import { EntetePage } from '../composants/Ui'
import { jourCourt } from '../libelles'

type Bilan = Awaited<ReturnType<typeof importer>>

function enBase64(octets: ArrayBuffer): string {
  const tableau = new Uint8Array(octets)
  let binaire = ''
  for (let i = 0; i < tableau.length; i += 0x8000) {
    binaire += String.fromCharCode(...tableau.subarray(i, i + 0x8000))
  }
  return btoa(binaire)
}

async function importer(compteId: string, fichier: File, dateExport: string) {
  return lire(
    api.releves.$post({
      json: {
        compteId,
        nomFichier: fichier.name,
        contenuBase64: enBase64(await fichier.arrayBuffer()),
        ...(dateExport && { dateExport }),
      },
    }),
  )
}

/** Comptes des entités actives, avec le nom de l'entité. */
function useComptes() {
  const entites = useQuery(requetes.entites)
  const actives = (entites.data ?? []).filter((e) => !e.archivee)
  const comptes = useQueries({ queries: actives.map((e) => requetes.comptes(e.id)) })
  return {
    charge: entites.data !== undefined,
    comptes: comptes.flatMap((q, i) =>
      (q.data ?? []).map((c) => ({ ...c, entite: actives[i]!.nom })),
    ),
  }
}

function ImportReleve() {
  const client = useQueryClient()
  const { charge, comptes } = useComptes()
  const tousComptes = comptes.filter((c) => c.source === 'csv')
  const [compteId, setCompteId] = useState('')
  const [fichier, setFichier] = useState<File | null>(null)
  const [dateExport, setDateExport] = useState('')
  const [bilan, setBilan] = useState<Bilan | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)

  async function soumettre(e: FormEvent) {
    e.preventDefault()
    if (!compteId || !fichier) return
    setEnCours(true)
    setErreur(null)
    setBilan(null)
    try {
      setBilan(await importer(compteId, fichier, dateExport))
      await client.invalidateQueries()
    } catch (err) {
      setErreur(err instanceof ErreurApi ? err.message : 'Import impossible.')
    } finally {
      setEnCours(false)
    }
  }

  return (
    <section className="carte" id="importer">
      <h2 className="titre-icone">
        <Icone nom="upload" />
        Importer un relevé
      </h2>
      <p className="discret">
        Export CSV du Crédit Agricole ou de Tiime. Le relevé est tenu pour couvert jusqu’à la veille
        de la date d’export (lue dans le fichier quand elle y figure, sinon aujourd’hui ou la date
        saisie).
      </p>
      {tousComptes.length === 0 && charge && (
        <Vide>Aucun compte alimenté par CSV : en créer un depuis la fiche d’une entité.</Vide>
      )}
      <form className="formulaire" onSubmit={soumettre} style={{ marginTop: 14 }}>
        <div className="champ">
          <label htmlFor="compte">Compte</label>
          <select
            id="compte"
            value={compteId}
            onChange={(e) => setCompteId(e.target.value)}
            required
          >
            <option value="">Choisir…</option>
            {tousComptes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.entite} · {c.libelle}
                {c.releveCouvertJusquAu
                  ? ` (couvert au ${formaterJour(c.releveCouvertJusquAu)})`
                  : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="export">Date de l’export (facultatif)</label>
          <input
            id="export"
            type="date"
            value={dateExport}
            onChange={(e) => setDateExport(e.target.value)}
          />
        </div>
        <label className="zone-depot" style={{ gridColumn: '1 / -1' }}>
          <Icone nom="csv" />
          <strong>{fichier ? fichier.name : 'Déposer le fichier CSV ici'}</strong>
          <span className="discret">{fichier ? 'ou en choisir un autre' : 'ou parcourir'}</span>
          <input
            type="file"
            aria-label="Fichier CSV"
            accept=".csv,text/csv"
            onChange={(e) => setFichier(e.target.files?.[0] ?? null)}
            required
          />
        </label>
        <div className="boutons">
          <button type="submit" className="bouton" disabled={enCours || !compteId || !fichier}>
            {enCours ? 'Import…' : 'Importer'}
          </button>
        </div>
      </form>
      {erreur && <p className="alerte">{erreur}</p>}
      {bilan && (
        <div className="section-ajout bilan-import">
          <p>
            <strong>{bilan.lignesNouvelles}</strong> opérations nouvelles sur {bilan.lignesLues}{' '}
            lues
            {bilan.doublons > 0 && `, ${bilan.doublons} déjà connues`}. Relevé couvert jusqu’au{' '}
            {formaterJour(bilan.couvertJusquAu)}.
          </p>
          <p>
            Rapprochement : {bilan.rapprochement.automatiques} automatiques,{' '}
            {bilan.rapprochement.decisions} décisions à trancher, {bilan.rapprochement.rejets}{' '}
            rejets
            {bilan.rapprochement.compensations > 0 &&
              `, ${bilan.rapprochement.compensations} paires d’opérations qui s’annulent`}
            .
          </p>
          {bilan.quasiDoublons.length > 0 && (
            <div className="alerte">
              À vérifier, même montant à deux jours près d’une autre opération :
              <ul>
                {bilan.quasiDoublons.map((q, i) => (
                  <li key={i}>
                    {formaterJour(q.date)} · {q.libelle} · {formaterMontant(q.montantCts)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {bilan.avertissements.length > 0 && (
            <ul className="discret">
              {bilan.avertissements.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  )
}

/** Au-delà d'une semaine sans relevé, la couverture d'un compte est signalée. */
const COUVERTURE_ANCIENNE_JOURS = 7

function Couverture() {
  const { comptes } = useComptes()
  const aujourdhui = jourAParis(new Date())
  return (
    <section className="carte">
      <h2 className="titre-icone">
        <Icone nom="account_balance" />
        Couverture des comptes
      </h2>
      {comptes.length === 0 && <Vide>Aucun compte.</Vide>}
      {comptes.map((c) => {
        const au = c.releveCouvertJusquAu
        const ancienne = !au || ecartJours(au, aujourdhui) > COUVERTURE_ANCIENNE_JOURS
        return (
          <div key={c.id} className="ligne-couverture">
            <LogoConnu nom={c.banque} />
            <div className="ligne-contenu">
              <span>{c.entite}</span>
              <span className="discret">
                {c.banque}, {c.libelle}
              </span>
            </div>
            {ancienne ? (
              <span className="badge grand ton-ambre">
                <Icone nom="schedule" />
                {au ? `au ${jourCourt(au)}` : 'jamais importé'}
              </span>
            ) : (
              <span className="couvert">
                <Icone nom="check_circle" />
                au {jourCourt(au)}
              </span>
            )}
          </div>
        )
      })}
    </section>
  )
}

export function Releves() {
  const imports = useQuery(requetes.imports)
  const transactions = useQuery(requetes.transactionsNonAffectees)
  const decisions = useQuery(requetes.decisions)
  const entites = useQuery(requetes.entites)
  const enDecision = new Set((decisions.data ?? []).map((d) => d.transactionId))
  // Dépenses courantes du foyer (courses, cartes) : cachées par défaut, elles noient celles
  // des sociétés (décision de Pierre-Marie du 12/10/2026).
  const [avecFoyer, setAvecFoyer] = useState(false)
  const foyers = new Set((entites.data ?? []).filter((e) => e.type === 'foyer').map((e) => e.id))
  const aRelier = (transactions.data ?? []).filter((t) => !enDecision.has(t.id))
  const duFoyer = aRelier.filter((t) => foyers.has(t.entiteId)).length
  const montrees = avecFoyer ? aRelier : aRelier.filter((t) => !foyers.has(t.entiteId))

  return (
    <main>
      <EntetePage
        surtitre="Banque"
        titre="Relevés"
        action={{
          icone: 'upload',
          libelle: 'Importer un relevé',
          onClick: () => {
            const zone = document.getElementById('importer')
            zone?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            document.getElementById('compte')?.focus({ preventScroll: true })
          },
        }}
      />
      <div className="deux-colonnes serre">
        <div className="colonne">
          <ImportReleve />
          <Couverture />
        </div>
        <div className="colonne">
          <section className="carte">
            <h2 className="titre-icone">
              <Icone nom="call_split" className="ambre" />
              Décisions à trancher
              {!!decisions.data?.length && (
                <span className="badge mono ton-ambre">{decisions.data.length}</span>
              )}
            </h2>
            {decisions.data?.length === 0 && <Vide>Aucune.</Vide>}
            {!!decisions.data?.length && (
              <p className="discret">
                L’appli ne sait pas quel paiement prévu ces opérations règlent : ouvrez-les une à
                une et répondez.
              </p>
            )}
            {decisions.data?.map((d) => (
              <Link
                key={d.id}
                to="/rapprochements/$id"
                params={{ id: d.transactionId! }}
                className="ligne ligne-lien ligne-operation"
              >
                <LogoConnu nom={d.libelle} />
                <div className="ligne-contenu">
                  <span>{d.libelle}</span>
                  <span className="discret">
                    {d.compte} · {jourCourt(d.dateOperation)}
                  </span>
                </div>
                <span className="montant">{formaterMontant(d.montantCts, d.devise)}</span>
                <span className="bouton petit">Répondre</span>
              </Link>
            ))}
          </section>

          <section className="carte">
            <h2 className="titre-icone">
              <Icone nom="link_off" />
              Opérations non rapprochées
            </h2>
            {transactions.data?.length === 0 && <Vide>Tout est rapproché.</Vide>}
            {montrees.length > 0 && (
              <p className="discret">
                Aucun paiement prévu n’y est relié. Ouvrez-en une pour la relier ou l’écarter.
              </p>
            )}
            {montrees.length === 0 && duFoyer > 0 && (
              <Vide>Rien à rapprocher pour les sociétés.</Vide>
            )}
            {duFoyer > 0 && (
              <div className="boutons bascule-foyer">
                <button
                  type="button"
                  className="bouton-discret"
                  aria-pressed={avecFoyer}
                  onClick={() => setAvecFoyer((x) => !x)}
                >
                  {avecFoyer
                    ? `Cacher les ${duFoyer} opérations du foyer`
                    : `Afficher aussi les ${duFoyer} opérations du foyer`}
                </button>
              </div>
            )}
            {montrees.map((t) => (
              <Link
                key={t.id}
                to="/rapprochements/$id"
                params={{ id: t.id }}
                className="ligne ligne-lien ligne-operation"
              >
                <LogoConnu nom={t.libelle} />
                <div className="ligne-contenu">
                  <span>
                    {t.libelle}
                    {t.rejet && <span className="badge ton-alerte">Rejet</span>}
                    {t.compensationProposee && (
                      <span className="badge ton-ambre">S’annule avec une autre ?</span>
                    )}
                  </span>
                  <span className="discret">
                    {t.compte} · {jourCourt(t.dateOperation)}
                  </span>
                </div>
                <span className="montant">{formaterMontant(t.resteCts, t.devise)}</span>
                <Icone nom="chevron_right" />
              </Link>
            ))}
          </section>

          <section className="carte">
            <h2 className="titre-icone">
              <Icone nom="history" />
              Imports
            </h2>
            {imports.data?.length === 0 && <Vide>Aucun import.</Vide>}
            {imports.data?.map((i) => (
              <div key={i.id} className="ligne ligne-operation">
                <Icone nom="description" />
                <div className="ligne-contenu">
                  <span style={{ overflowWrap: 'anywhere' }}>{i.nomFichier}</span>
                  <span className="discret">
                    {i.compte} · {i.format === 'tiime' ? 'Tiime' : 'Crédit Agricole'} · du{' '}
                    {i.du && jourCourt(i.du)} au {i.au && jourCourt(i.au)} · export du{' '}
                    {formaterJour(i.dateExport)} · {formaterInstant(i.creeLe)}
                  </span>
                </div>
                <span className="mono" style={{ fontSize: 12, color: 'var(--accent)' }}>
                  {i.lignesNouvelles}/{i.lignesLues}
                </span>
              </div>
            ))}
          </section>
        </div>
      </div>
    </main>
  )
}
